import { useMemo } from 'react';

import { CellContext, ColumnDef } from '@tanstack/react-table';
import { DATE_TIME_FORMAT } from 'constants/constants';
import dayjs from 'dayjs';
import { OFFER_TYPE_DISPLAY_NAMES } from 'views/OfferDesign/OfferDesign.consts';
import {
  OfferDesignActions,
  OfferDesignPermissions,
  OfferDesignRow
} from 'views/OfferDesign/OfferDesign.types';

import ActionCell from 'components/DataTableComponent/components/ActionCell';

interface UseOfferDesignColumnsProps {
  isLoading: boolean;
  actions: OfferDesignActions;
  permissions: OfferDesignPermissions;
}

export const useOfferDesignColumns = ({
  isLoading,
  actions,
  permissions
}: UseOfferDesignColumnsProps) => {
  const columns = useMemo((): ColumnDef<OfferDesignRow>[] => {
    return [
      {
        accessorKey: 'image',
        header: 'Image',
        cell: (info: CellContext<OfferDesignRow, unknown>) => {
          return info.row.original.backgroundImage ? (
            <img
              className="object-cover w-10 h-10"
              src={info.row.original.backgroundImage}
              alt="offer design"
            />
          ) : (
            <span className="text-zinc-400">N/A</span>
          );
        }
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info: CellContext<OfferDesignRow, unknown>) => (
          <span className="truncate max-w-[200px] block">
            {String(info.getValue())}
          </span>
        ),
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const nameA = rowA.original.name || '';
          const nameB = rowB.original.name || '';
          return nameA.localeCompare(nameB);
        }
      },
      {
        accessorKey: 'externalId',
        header: 'External ID',
        cell: (info: CellContext<OfferDesignRow, unknown>) => (
          <span className="truncate">{String(info.getValue())}</span>
        )
      },
      {
        accessorKey: '_id',
        header: 'ID',
        cell: (info: CellContext<OfferDesignRow, unknown>) => (
          <span className="truncate">{String(info.getValue())}</span>
        )
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: (info: CellContext<OfferDesignRow, unknown>) => {
          if (isLoading) return '';
          return (
            <span className="whitespace-nowrap">
              {dayjs(info.row.original.createdAt).utc().format(DATE_TIME_FORMAT)}
            </span>
          );
        },
        sortDescFirst: true,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const dateA = rowA.original.createdAt;
          const dateB = rowB.original.createdAt;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        }
      },
      {
        accessorKey: 'offerUiType',
        header: 'Type',
        cell: (info: CellContext<OfferDesignRow, unknown>) => {
          const raw = String(info.getValue());
          return OFFER_TYPE_DISPLAY_NAMES[raw] ?? raw;
        }
      },
      {
        accessorKey: 'actions',
        header: '',
        cell: (info: CellContext<OfferDesignRow, unknown>) => (
          <ActionCell
            rowData={info.row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => actions.onEdit(info.row.original._id, info.row.original.offerUiType),
                disabled:
                  !permissions.canUserEdit || !permissions.canManage
              },
              {
                label: 'Duplicate',
                handler: () => actions.onDuplicate(info.row.original._id, info.row.original.offerUiType),
                disabled:
                  !permissions.canUserEdit || !permissions.canManage
              },
              {
                label: 'Delete',
                handler: () => actions.onDelete(info.row.original._id),
                disabled:
                  !permissions.canUserEdit || !permissions.canManage
              }
            ]}
          />
        )
      }
    ];
  }, [isLoading, actions, permissions]);

  return { columns };
};
